import React, { useState } from 'react';
import { BaseInput, type BaseInputProps } from './';

type TelInputProps = Omit<BaseInputProps, 'stepper'> & {
  countryCode?: string;
  value?: string;
  onValueChange?: (value: string) => void;
};

// Function to generate the pattern according to the countryCode
const getPatternByCountryCode = (code: string) => {
  switch (code) {
    case 'US':
      return 'XXX-XXX-XXXX'; // EE.UU.: 123-456-7890
    case 'FR':
      return 'X XX XX XX XX'; // France: 12 34 56 78 90
    case 'UK':
      return 'XXXXX XXXXXX'; // UK: XXXXX XXXXXX
    case 'ES':
      return 'XXX XXX XXX'; // Spain: XXX XXX XXX
    default:
      return 'XXXXXXXXXX'; // Generic pattern
  }
};

// Function to format phone number according to pattern
const formatPhoneNumberWithPattern = (value: string, pattern: string) => {
  const cleaned = value.replace(/\D/g, ''); // Remeve non-numeric characters
  let formattedValue = '';
  let cleanIndex = 0;

  // go through the pattern and apply it to the entered number
  for (let i = 0; i < pattern.length; i++) {
    if (pattern[i] === 'X' && cleanIndex < cleaned.length) {
      formattedValue += cleaned[cleanIndex];
      cleanIndex++;
    } else if (pattern[i] !== 'X') {
      formattedValue += pattern[i]; // Add separators such as parentheses or dashes
    }
    if (cleanIndex >= cleaned.length) {
      break; // Stop when there are no more numbers to add
    }
  }

  return formattedValue;
};

const TelInput = React.forwardRef<HTMLInputElement, TelInputProps>(
  (props, ref) => {
    const { countryCode, value = '', onValueChange, ...other } = props;

    // const [formattedPhone, setFormattedPhone] = useState('');
    const [formattedPhone, setFormattedPhone] = useState(
      formatPhoneNumberWithPattern(
        value,
        getPatternByCountryCode(countryCode || 'US'),
      ),
    );
    // const [cleanPhone, setCleanPhone] = useState('');

    // Change handler
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      let rawValue = e.target.value;

      // Remove the first character if it is a "0"
      if (rawValue.startsWith('0')) {
        rawValue = rawValue.slice(1);
      }

      // Get the pattern based on the countryCode
      const pattern = getPatternByCountryCode(countryCode || '');

      // Format the number according to the pattern
      const formattedValue = formatPhoneNumberWithPattern(rawValue, pattern);
      setFormattedPhone(formattedValue);

      // Save the clean version (numbers only) to send to the database
      let cleanedValue = rawValue.replace(/\D/g, '');
      if (countryCode) {
        const maxDigits = (pattern.match(/X/g) || []).length;
        cleanedValue = cleanedValue.slice(0, maxDigits);
      }

      // If onValueChange is defined, the value in the parent component is updated
      if (onValueChange) {
        onValueChange(cleanedValue);
      }
    };

    return (
      <div>
        <BaseInput
          ref={ref}
          type="tel"
          value={formattedPhone}
          onChange={handleChange}
          placeholder={getPatternByCountryCode(countryCode || '').replace(
            /X/g,
            '0',
          )} // Show the pattern as placeholder
          {...other}
        />
      </div>
    );
  },
);

TelInput.displayName = 'TelInput';

export { TelInput, type TelInputProps };
